import { useQuery } from '@tanstack/react-query';
import { ChartFrame, ChartTable, PlanBars, StatusList } from '../../components/charts';
import { adminApi, formatCount, formatMoney } from '../../lib/adminApi';
import { usePageTitle } from '../../lib/usePageTitle';
import { AdminPanelState, StatTile } from './AdminLayout';

/**
 * The subscription picture.
 *
 * Reads the **same** dashboard snapshot rather than its own aggregate — the two
 * screens show overlapping counts, and two queries could disagree about how many
 * subscriptions are active while both were technically correct about different
 * instants. React Query serves it from the cache when the operator arrives here
 * from the dashboard, so the extra call usually does not happen either.
 */
export function AdminSubscriptions() {
  usePageTitle('Subscriptions — WaveQA Admin');

  const dashboard = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => adminApi.getDashboard(),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white">Subscriptions</h1>
        <p className="mt-1 text-sm text-hull-400">
          Where the book stands, and which plans carry it.
        </p>
      </div>

      <AdminPanelState loading={dashboard.isPending} error={dashboard.error}>
        {dashboard.data && (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatTile
                label="Active"
                value={formatCount(dashboard.data.subscriptions.active)}
                hint="entitled and billing"
              />
              <StatTile
                label="Trialling"
                value={formatCount(dashboard.data.subscriptions.trialing)}
                hint="occupying a plan, not yet billing"
              />
              <StatTile
                label="Cancelled"
                value={formatCount(dashboard.data.subscriptions.canceled)}
              />
              <StatTile
                label="Expired"
                value={formatCount(dashboard.data.subscriptions.expired)}
                hint="paid period has ended"
              />
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <ChartFrame
                title="Every state"
                caption="Active and expired are derived from entitlement, not read from the status column — a subscription whose period has ended is already being refused by the agent endpoints, whatever its stored status says."
              >
                <StatusList
                  entries={[
                    {
                      label: 'Active',
                      value: dashboard.data.subscriptions.active,
                      tone: 'active',
                    },
                    {
                      label: 'Trialling',
                      value: dashboard.data.subscriptions.trialing,
                      tone: 'trialing',
                    },
                    {
                      label: 'Past due',
                      value: dashboard.data.subscriptions.past_due,
                      tone: 'cancelled',
                      hint: 'Payment failed; the provider is retrying.',
                    },
                    {
                      label: 'Paused',
                      value: dashboard.data.subscriptions.paused,
                      tone: 'neutral',
                    },
                    {
                      label: 'Cancelled',
                      value: dashboard.data.subscriptions.canceled,
                      tone: 'cancelled',
                    },
                    {
                      label: 'Expired',
                      value: dashboard.data.subscriptions.expired,
                      tone: 'expired',
                    },
                    {
                      label: 'Awaiting payment',
                      value: dashboard.data.subscriptions.pending,
                      tone: 'neutral',
                      hint: 'Checkout started, no verified payment event yet.',
                    },
                  ]}
                />
              </ChartFrame>

              <ChartFrame
                title="Plan occupancy"
                caption="Accounts on each plan, active and trialling."
              >
                <PlanBars
                  slices={dashboard.data.plans.map((plan) => ({
                    label: plan.display_name,
                    value: plan.subscribers,
                  }))}
                />
              </ChartFrame>
            </div>

            <section className="rounded-card border border-hull-700 bg-hull-800/60 p-5 sm:p-6">
              <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-hull-300">
                Revenue by plan
              </h2>
              <p className="mt-1.5 text-xs leading-relaxed text-hull-400">
                Contracted monthly revenue: the catalogue price times the accounts
                actually billing. Trials are excluded, so this column sums to MRR
                rather than to an aspiration.
              </p>

              <div className="mt-5 overflow-x-auto">
                <ChartTable
                  columns={['Plan', 'Accounts', 'Billing', 'Monthly revenue']}
                  rows={dashboard.data.plans.map((plan) => [
                    plan.display_name,
                    plan.subscribers,
                    plan.billing_subscribers,
                    formatMoney(plan.monthly_revenue_minor_units, dashboard.data.currency, {
                      exact: true,
                    }),
                  ])}
                />
              </div>

              <p className="mt-4 flex flex-wrap items-baseline justify-between gap-2 border-t border-hull-700 pt-4 text-sm">
                <span className="text-hull-400">Total MRR</span>
                <span className="text-lg font-semibold tabular-nums text-white">
                  {formatMoney(dashboard.data.mrr.minor_units, dashboard.data.currency, {
                    exact: true,
                  })}
                </span>
              </p>
            </section>
          </div>
        )}
      </AdminPanelState>
    </div>
  );
}
